import React from "react";
import { Chip, alpha, styled } from "@mui/material";
import { AVAILABILITY, AvailabilityInput, resolveAvailability } from "../../utils/availability";
import { AVAILABILITY_TEXTS } from "../../pages/shared/constants";

/**
 * The one-word answer to "can I buy this today?", on every surface that lists a product.
 *
 * One component rather than a chip per screen, because the card, the product page and the cart
 * line have to agree: a shopper who sees "Disponible" in a grid and "Bajo pedido" after clicking
 * has been misled by whichever one was written second.
 */

type Tone = "inStock" | "backorder" | "quote";

const TONE_BY_STATE: Record<string, Tone> = {
  [AVAILABILITY.IN_STOCK]: "inStock",
  [AVAILABILITY.BACKORDER]: "backorder",
  [AVAILABILITY.QUOTE]: "quote",
};

// Tinted rather than filled: these sit next to the yellow and orange buy buttons, and three more
// solid colours in the same block turn a product card into a paint chart.
const StateChip = styled(Chip, { shouldForwardProp: (prop) => prop !== "tone" })<{ tone: Tone }>(
  ({ theme, tone }) => ({
    color: theme.palette.purchase[tone],
    backgroundColor: alpha(theme.palette.purchase[tone], 0.12),
    fontWeight: 700,
    height: 22,
    "& .MuiChip-label": { paddingInline: theme.spacing(1) },
  }),
);

type AvailabilityChipProps = {
  item: AvailabilityInput;
  size?: "small" | "medium";
  className?: string;
};

const AvailabilityChip: React.FC<AvailabilityChipProps> = ({ item, size = "small", className }) => {
  const state = resolveAvailability(item);

  // A deactivated product has no promise to make and never reaches a storefront surface anyway —
  // the listing and the detail page both filter on `is_active` upstream (EB-503). Rendering
  // nothing is better than inventing a fourth label for a case only the admin preview can hit.
  const tone = TONE_BY_STATE[state];
  if (!tone) return null;

  return (
    <StateChip
      tone={tone}
      size={size}
      className={className}
      label={AVAILABILITY_TEXTS.chip[state]}
    />
  );
};

export default AvailabilityChip;
